import { useGetCurrentUserQuery } from '@loci/api';
import type { User } from '@loci/api';

/**
 * Check if an RTK Query error is a 401 authentication error.
 * 401 responses indicate "unauthenticated" state, not an error condition.
 */
function isAuthenticationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ('status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' && status === 401;
  }

  return false;
}

/**
 * Auth session state returned by useAuthSession hook.
 */
export interface AuthSessionState {
  /** The authenticated user, or null if not authenticated */
  user: User | null;
  /** Whether the user is authenticated (user !== null) */
  isAuthenticated: boolean;
  /** Whether the auth state is currently loading */
  isLoading: boolean;
  /** Whether there was an error (excludes 401 which is treated as unauthenticated) */
  isError: boolean;
  /** Function to refetch the current user */
  refetch: () => void;
}

/**
 * Hook that provides ergonomic auth session state derived from RTK Query.
 *
 * This hook wraps useGetCurrentUserQuery and provides a standardized auth state interface.
 * It treats 401 responses as "unauthenticated" (user=null) rather than error states,
 * reserving isError for network errors, 5xx errors, and parsing errors.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, isLoading, isError, refetch } = useAuthSession();
 *
 *   if (isLoading) return <Loading />;
 *   if (isError) return <Error />;
 *   if (!isAuthenticated) return <LoginPrompt />;
 *
 *   return <UserProfile user={user} />;
 * }
 * ```
 */
export function useAuthSession(): AuthSessionState {
  const { data, isLoading, error, refetch } = useGetCurrentUserQuery();

  const user = data?.user ?? null;
  const isAuthenticated = user !== null;
  const isError = !!error && !isAuthenticationError(error);

  return { user, isAuthenticated, isLoading, isError, refetch };
}
